/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { useState } from 'react';
import * as React from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { BaseTextFieldProps } from '@mui/material/TextField';
import { isEqual } from 'lodash';
import { FormHelperText } from '@mui/material';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            '& > * + *': {
                marginTop: theme.spacing(3),
            },
        },
        option: {
            '&[aria-selected="true"]': {
                backgroundColor: '#EBEBEB!important',
            },
            '&[aria-selected="true"].Mui-focused': {
                backgroundColor: '#F5F5F5!important',
            },
        },
        hint: {
            color: theme.palette.primary.main,
            lineHeight: 1.3,
        },
    }),
);

export interface MultiSearchSelectProps<T> {
    options: Array<T>;
    selected?: Array<T>;
    label: string;
    disabled?: boolean;
    outlined?: boolean;
    isError?: boolean;
    hint?: string;
    getOptionLabel(value: T): string;
    onChange(value: T[]): void;
    onBlur?(): void;
}

export function MultiSearchSelect<T>(props: MultiSearchSelectProps<T>) {
    const classes = useStyles();
    const { options, getOptionLabel, onChange, onBlur, label, selected, disabled, outlined, isError, hint } = props;

    const [selectedValues, addValue] = useState(selected.length !== 0 ? (selected as T[]) : []);

    React.useEffect(() => {
        addValue(selected);
    }, [selected]);

    return (
        <>
            <div className={classes.root}>
                <Autocomplete
                    disabled={disabled ? true : null}
                    multiple
                    options={options}
                    isOptionEqualToValue={(option, value) => isEqual(option, value)}
                    value={selectedValues}
                    classes={{ option: classes.option }}
                    getOptionLabel={(option) => getOptionLabel(option)}
                    renderInput={(params) => (
                        <TextField
                            {...(params as BaseTextFieldProps)}
                            variant={outlined ? 'outlined' : 'standard'}
                            label={label}
                            error={isError}
                        />
                    )}
                    onChange={(_event, value) => {
                        addValue(value as T[]);
                        onChange(value as T[]);
                    }}
                    size={'small'}
                    onBlur={onBlur}
                />
            </div>
            {hint && <FormHelperText className={classes.hint}>{hint}</FormHelperText>}
        </>
    );
}
